import { useMemo } from 'react';

import api from 'apis/apiHelper';
import { useCustomQuery } from 'hooks/reactQuery';

/* ================================================ Exported Functions ================================================ */
export const useFetchConstant = (name, { valueKey = 'code', labelKey = 'label', extraCustomKey = '', query = {}, postProcessFunc } = {}) => {
  const getConstant = async () => {
    return api.get(`/constants/${name}`, { params: query });
  };

  const { isLoading, data: constantObject, ...returnedParams } = useCustomQuery(`constant${name}${extraCustomKey}`, [name, query], getConstant, {
    postProcessFunc,
    prefixErrorMessage: `Error while getting ${name}.\n`,
    staleTime: Infinity
  });

  const shouldConstructOption = !isLoading && !!constantObject;
  const shouldDeduplicateValues = name === 'countries' && valueKey === 'phoneCode';

  const options = useMemo(() => constructOptions(shouldConstructOption, constantObject, valueKey, labelKey, shouldDeduplicateValues), [
    shouldConstructOption,
    constantObject,
    valueKey,
    labelKey,
    shouldDeduplicateValues
  ]);

  return { isLoading, options, data: constantObject, ...returnedParams };
};

/* ================================================ Local Functions ================================================ */
const constructOptions = (shouldConstruct, constantObject, valueKey, labelKey, shouldDeduplicateValues) => {
  if (!shouldConstruct) {
    return [];
  }

  if (shouldConstruct) {
    const options = Object.values(constantObject).map(option => ({
      ...option,
      value: option[valueKey],
      label: option[labelKey]
    }));

    if (!shouldDeduplicateValues) {
      return options;
    }

    return options.reduce((uniqueOptions, option) => {
      if (!uniqueOptions.find(uniqueOption => uniqueOption.value === option.value)) {
        uniqueOptions.push(option);
      }

      return uniqueOptions;
    }, []);
  }
};
